/**
 * POSGroupDashboard - Customer Group Dashboard
 * Shows aggregate stats across all properties under a customer group.
 * Accessible to customer-group managers and CMB staff.
 */

import { useState, useEffect, useMemo } from 'react';
import { formatDayMonth } from '../../../utils/formatters';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { usePOS } from '../../../contexts/POSContext';
import EmptyState from '../../../components/EmptyState';

import {
  POS_GOLD,
  calculateRoomStatus,
} from '../../../config/posRoomStatus';

interface RoomWithStatus {
  id: string;
  room_number: string;
  property_id: string;
  bed_count: number;
  last_service_date: string | null;
  status: string;
}

function POSGroupDashboard() {
  const { customerSlug } = useParams<{ customerSlug: string }>();
  const navigate = useNavigate();
  const { supabase } = useAuth();
  const { userProperties, groups } = usePOS();

  const [rooms, setRooms] = useState<RoomWithStatus[]>([]);
  const [serviceRequests, setServiceRequests] = useState<Record<string, unknown>[]>([]);
  const [managers, setManagers] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);

  // Find the customer group from the slug
  const group = useMemo(() => {
    const slugMatch = customerSlug?.toLowerCase().replace(/-/g, ' ');
    return groups.find(
      (g) => g.group_name.toLowerCase() === slugMatch
    );
  }, [customerSlug, groups]);

  // Filter properties belonging to this customer
  const groupProperties = useMemo(() => {
    if (!group) return [];
    return userProperties.filter((p) => p.group_id === group.group_id);
  }, [userProperties, group]);

  // Load aggregate data
  useEffect(() => {
    if (!supabase || !groupProperties.length) {
      setLoading(false);
      return;
    }

    const propertyIds = groupProperties.map((p) => p.id);

    async function loadData() {
      setLoading(true);

      // Load rooms
      const { data: roomData } = await supabase!
        .from('hosp_rooms')
        .select('id, room_number, property_id, bed_count, last_service_date, status')
        .in('property_id', propertyIds)
        .eq('status', 'active');

      // Load service requests
      const { data: requestData } = await supabase!
        .from('hosp_service_requests')
        .select('id, property_id, requested_date, requested_date_end, room_ids, total_beds, status, preferred_time')
        .in('property_id', propertyIds)
        .in('status', ['pending', 'confirmed'])
        .order('requested_date', { ascending: true });

      // Load managers
      const { data: managerData } = await supabase!
        .from('hosp_property_managers')
        .select('id, property_id, access_level, users ( first_name, last_name, email )')
        .in('property_id', propertyIds);

      setRooms((roomData || []) as RoomWithStatus[]);
      setServiceRequests(requestData || []);
      setManagers(managerData || []);
      setLoading(false);
    }

    loadData();
  }, [supabase, groupProperties]);

  // Calculate room statuses using shared utility
  const roomStats = useMemo(() => {
    let certified = 0;
    let dueSoon = 0;
    let overdue = 0;
    let notServiced = 0;
    let totalBeds = 0;

    for (const room of rooms) {
      totalBeds += room.bed_count || 1;
      const prop = groupProperties.find((p) => p.id === room.property_id);
      const tier = prop?.hospitality_tier || 'assured';
      const status = calculateRoomStatus(room.last_service_date, tier);

      if (status === 'not-serviced') notServiced++;
      else if (status === 'overdue') overdue++;
      else if (status === 'due-soon') dueSoon++;
      else certified++;
    }

    return { certified, dueSoon, overdue, notServiced, totalBeds, totalRooms: rooms.length };
  }, [rooms, groupProperties]);

  // Coverage percentage
  const coveragePct = rooms.length > 0
    ? Math.round((roomStats.certified / rooms.length) * 100)
    : 0;

  if (!group) {
    return (
      <EmptyState
        message="Customer group not found"
        action={
          <button onClick={() => navigate('/pos')} style={{ color: POS_GOLD, background: 'none', border: 'none', cursor: 'pointer', fontSize: 15 }}>
            Back to Properties
          </button>
        }
      />
    );
  }

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-light)' }}>
        Loading group data...
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <button
            onClick={() => navigate('/pos')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--color-text-light)' }}
          >
            All Properties
          </button>
          <span style={{ color: 'var(--color-border)' }}>/</span>
          <span style={{ fontSize: 14, color: 'var(--color-text-light)' }}>{group.group_name}</span>
        </div>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 600 }}>
          {group.group_name}
        </h1>
        <p style={{ margin: '4px 0 0', color: 'var(--color-text-light)', fontSize: 15 }}>
          {groupProperties.length} {groupProperties.length === 1 ? 'property' : 'properties'}
        </p>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 32 }}>
        <SummaryCard label="Properties" value={groupProperties.length} />
        <SummaryCard label="Total Rooms" value={roomStats.totalRooms} />
        <SummaryCard label="Total Beds" value={roomStats.totalBeds} />
        <SummaryCard label="Certified" value={roomStats.certified} color="#16a34a" />
        <SummaryCard label="Due Soon" value={roomStats.dueSoon} color="var(--color-warning)" />
        <SummaryCard label="Overdue" value={roomStats.overdue} color="#dc2626" />
      </div>

      {/* Coverage Bar */}
      <div style={{ background: 'var(--color-bg)', borderRadius: 8, padding: 20, marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>Service Coverage</span>
          <span style={{ fontWeight: 700, fontSize: 14, color: coveragePct >= 80 ? 'var(--color-success)' : coveragePct >= 50 ? 'var(--color-warning)' : 'var(--color-error)' }}>
            {coveragePct}%
          </span>
        </div>
        <div style={{ height: 8, background: 'var(--color-border)', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${coveragePct}%`, background: coveragePct >= 80 ? 'var(--color-success)' : coveragePct >= 50 ? '#d97706' : 'var(--color-error)', borderRadius: 4, transition: 'width 0.3s' }} />
        </div>
      </div>

      {/* Properties Grid */}
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Properties</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16, marginBottom: 32 }}>
        {groupProperties.map((prop) => {
          const propRooms = rooms.filter((r) => r.property_id === prop.id);
          const propRequests = serviceRequests.filter((sr) => (sr as { property_id: string }).property_id === prop.id);
          const slug = prop.property_code?.toLowerCase() || prop.property_name.toLowerCase().replace(/\s+/g, '-');

          return (
            <Link
              key={prop.id}
              to={`/pos/${slug}`}
              style={{
                display: 'block', padding: 20, background: 'var(--color-white)', border: '1px solid #e5e7eb',
                borderRadius: 8, textDecoration: 'none', color: 'inherit', transition: 'border-color 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = POS_GOLD)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--color-border)')}
            >
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{prop.property_name}</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-light)', marginBottom: 12 }}>
                {prop.hospitality_tier && (
                  <span style={{ textTransform: 'capitalize' }}>{prop.hospitality_tier}</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 16, fontSize: 13, color: 'var(--color-text-light)' }}>
                <span>{propRooms.length} rooms</span>
                {propRequests.length > 0 && (
                  <span style={{ color: POS_GOLD }}>{propRequests.length} pending</span>
                )}
              </div>
            </Link>
          );
        })}
      </div>

      {/* Upcoming Service Requests */}
      {serviceRequests.length > 0 && (
        <>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Upcoming Service Requests</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32 }}>
            {serviceRequests.slice(0, 10).map((sr) => {
              const prop = groupProperties.find((p) => p.id === (sr as { property_id: string }).property_id);
              const reqDate = sr.requested_date as string;
              const reqDateEnd = sr.requested_date_end as string | null;
              const dateDisplay = reqDateEnd ? `${reqDate} — ${reqDateEnd}` : reqDate;

              return (
                <div key={sr.id as string} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 16, background: 'var(--color-bg)', borderRadius: 8 }}>
                  <div style={{ minWidth: 80, padding: '8px 12px', background: POS_GOLD, borderRadius: 6, textAlign: 'center', color: 'var(--color-white)', fontSize: 13, fontWeight: 600 }}>
                    {formatDayMonth(reqDate + 'T12:00:00')}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{prop?.property_name || 'Unknown'}</div>
                    <div style={{ fontSize: 13, color: 'var(--color-text-light)' }}>
                      {(sr.room_ids as string[])?.length || 0} rooms, {sr.total_beds as number || 0} beds
                      {reqDateEnd && <span> &middot; {dateDisplay}</span>}
                    </div>
                  </div>
                  <span style={{
                    padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
                    background: (sr.status as string) === 'confirmed' ? 'var(--color-info-bg)' : 'var(--color-warning-bg)',
                    color: (sr.status as string) === 'confirmed' ? 'var(--color-info-text)' : 'var(--color-warning-text)',
                  }}>
                    {sr.status as string}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Staff Overview */}
      {managers.length > 0 && (
        <>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Staff</h2>
          <div style={{ background: 'var(--color-bg)', borderRadius: 8, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600, color: 'var(--color-text-light)' }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600, color: 'var(--color-text-light)' }}>Property</th>
                  <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600, color: 'var(--color-text-light)' }}>Access</th>
                </tr>
              </thead>
              <tbody>
                {managers.map((m) => {
                  const mUser = m.users as { first_name: string; last_name: string; email: string } | null;
                  const prop = groupProperties.find((p) => p.id === (m.property_id as string));
                  return (
                    <tr key={m.id as string} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '10px 16px' }}>
                        <div style={{ fontWeight: 500 }}>{mUser?.first_name} {mUser?.last_name}</div>
                        <div style={{ fontSize: 12, color: 'var(--color-text-light)' }}>{mUser?.email}</div>
                      </td>
                      <td style={{ padding: '10px 16px', color: 'var(--color-text-light)' }}>{prop?.property_name || '—'}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                          textTransform: 'capitalize',
                          background: 'rgba(189, 152, 47, 0.15)', color: POS_GOLD,
                        }}>
                          {m.access_level as string}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div style={{ background: 'var(--color-bg)', borderRadius: 8, padding: 20, textAlign: 'center' }}>
      <div style={{ fontSize: 28, fontWeight: 700, color: color || 'var(--color-sidebar)', marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
    </div>
  );
}

export default POSGroupDashboard;
